/**
 * Format the date and time in the format "day of the week, day of the month, month, year, hour, minute"
 * @param date - The date as ISO string
 * @returns example: "Lun. 1er septembre 2025 à 09:00"
 */
export const formatDateAndTime = (date: string): string =>
  new Date(date).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Format the date and time in the format "day of the week, day of the month, month, year, hour, minute"
 * @param date - The date as ISO string
 * @returns example: "Lundi 1er septembre 2025 à 09:00"
 */
export const formatDateAndTimeWithLongWeekday = (date: string): string =>
  new Date(date).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Format the date in the format "day of the month, month, year"
 * @param date - The date as ISO string
 * @returns example: "1er septembre 2025" or "-"
 */
export const formatDateShort = (date: string | null): string => {
  if (!date || date.trim() === "") {
    return "-";
  }
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Format the date and time in the format "day of the week, day of the month, month, year, hour, minute"
 * @param date - The date as ISO string
 * @returns example: ["Lun. 1er septembre 2025", "09:00"]
 */
export const formatAndSplitDateAndTime = (date: string): [string, string] => {
  const splittedDate = formatDateAndTime(date).split(" ");
  const time = splittedDate.at(-1);
  const formattedDate = splittedDate.slice(0, -2).join(" ");
  return [formattedDate, time ?? ""];
};

/**
 * Format the date and time in the format "day of the week, day of the month, month, year, hour, minute"
 * @param date - The date as ISO string
 * @returns example: ["Lundi 1er septembre 2025", "09:00"]
 */
export const formatAndSplitDateAndTimeWithLongWeekday = (date: string): [string, string] => {
  const splittedDate = formatDateAndTimeWithLongWeekday(date).split(" ");
  const time = splittedDate.at(-1);
  const formattedDate = splittedDate.slice(0, -2).join(" ");
  return [formattedDate, time ?? ""];
};

/**
 * Check if a date is in the past
 * @param date - The date as ISO string
 * @returns true if the date is in the past, false otherwise
 */
export const isInPast = (date: string): boolean => {
  return new Date(date) < new Date();
};

/**
 * Get the today's date as ISO string
 * @returns example: "2025-12-22"
 */
export const getTodayDateString = (): string => {
  return new Date().toISOString().split("T")[0];
};

/**
 * Today's date as an ISO string in Europe/Paris — the server's Time.zone. Browser-
 * local getFullYear/getDate can disagree near Paris midnight or for an admin in
 * another timezone, producing off-by-a-day 422s against the server's date guards.
 * Use for the refund filing-date / reattribution pickers, whose bounds the server
 * enforces in Europe/Paris.
 * @returns example: "2026-06-10"
 */
export const getTodayParisDateString = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/**
 * The refund override / reattribution floor: the start of last calendar year in
 * Europe/Paris, mirroring the server's filing_date_override_floor /
 * reference_date_floor. Shared so the picker clamp can't drift from the guard.
 * @returns example: "2025-01-01"
 */
export const refundDateFloorIso = (): string =>
  `${Number(getTodayParisDateString().slice(0, 4)) - 1}-01-01`;

/**
 * Format a date-only ISO string ("YYYY-MM-DD") as "DD/MM/YYYY" without going
 * through `new Date()`. A bare date string parses as UTC midnight, which
 * `toLocaleDateString` then renders in the browser's zone — rolling back a day
 * for admins west of UTC. The refund filing-date / reattribution labels carry
 * date-only values the server anchors in Europe/Paris, so they must display the
 * calendar date verbatim, matching the picker bounds beside them.
 * @returns example: "10/06/2026" or the fallback
 */
export const formatIsoDateLocale = (isoDate: string | null, fallback = "—"): string => {
  if (!isoDate) return fallback;
  const [year, month, day] = isoDate.slice(0, 10).split("-");
  if (!year || !month || !day) return fallback;
  return `${day}/${month}/${year}`;
};

/**
 * Format the date in relative date format
 * @param date - The date as ISO string
 * @returns example: "aujourd'hui" or "hier" or formatDateShort
 */
export const formatRelativeDateToNow = (date: string): string => {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const diffInDays = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (diffInDays === 0) return "aujourd'hui";
  if (diffInDays === 1) return "hier";
  return formatDateShort(date);
};

/**
 * Calculate age in full years from a birth date
 * @param birthDate - The birth date as ISO string (e.g. "2007-07-09")
 * @returns The age in years, or null if the date is invalid
 */
export const calculateAge = (birthDate: string): number | null => {
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

/**
 * Format age from a birth date, showing months for children under 1 year
 * @param birthDate - The birth date as ISO string (e.g. "2026-01-15")
 * @returns Formatted age string (e.g. "18 ans", "4 mois") or null if invalid
 */
export const formatTransferDate = (isoDate: string): string | null => {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `Transféré le ${day} à ${hours}h${minutes}`;
};

export const formatAge = (birthDate: string): string | null => {
  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return null;

  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();

  if (today.getDate() < birth.getDate()) {
    months--;
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  if (years < 1) {
    return `${months} mois`;
  }

  return `${years} ans`;
};
